import {
  ITaskTemplate,
  ITimelineItem
} from '@dadohr/shared/domainTypes/experience';
import { Typography } from '@material-ui/core';
import { compact, groupBy } from 'lodash';
import { CornerDownRight } from 'lucide-react';
import React, { useMemo } from 'react';
import { TimelineIcon } from '../../../../../../components/CustomIcons';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { TriggeredItemsList } from '../../../TriggeredItemsList';
import { TaskBuilderContext } from '../types';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';

const ICON = TimelineIcon;

const getTriggeredItemCountsText = (
  counts: {
    tasks: number;
    messages: number;
    automations: number;
  },
  t: UnsafeTFunction
) => {
  return compact([
    counts.tasks ? t('counters.tasks', { count: counts.tasks }) : null,
    counts.messages ? t('counters.messages', { count: counts.messages }) : null,
    counts.automations
      ? t('counters.automations', { count: counts.automations })
      : null
  ]).join(' • ');
};

export const TaskSettingsOverviewSectionTriggeredItems = ({
  title,
  triggeredItems,
  onEdit,
  divider,
  tplContext,
  onGoToOtherItem,
  hideIcon
}: {
  title: string;
  triggeredItems: ITimelineItem[];
  onEdit?: () => void;
  divider?: boolean;
  tplContext: TaskBuilderContext;
  onGoToOtherItem?: (item: ITimelineItem) => void;
  hideIcon?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  const byType = useMemo(() => groupBy(triggeredItems, (x) => x.itemType), [
    triggeredItems
  ]);

  if (!triggeredItems.length) {
    return null;
  }

  const counts = {
    tasks: (byType['TASK'] || []).length,
    messages: (byType['MSG'] || []).length,
    automations: (byType['AUTOMATION'] || []).length
  };

  const showOnlyCounts = !!onEdit;

  return (
    <TaskSettingsOverviewSection
      icon={hideIcon ? undefined : (ICON as any)}
      title={title}
      onEdit={onEdit}
      divider={divider}
      sayViewNotEdit
    >
      {showOnlyCounts ? (
        <FlexContainer justifyContent="flex-start" spacing={0.5} fullWidth>
          <Typography variant="caption">
            <CornerDownRight size={14} />
          </Typography>
          <Typography variant="caption">
            {getTriggeredItemCountsText(counts, t)}
          </Typography>
        </FlexContainer>
      ) : (
        <TriggeredItemsList
          triggeredItems={triggeredItems}
          tplContext={tplContext}
          onGoToOtherItem={onGoToOtherItem}
        />
      )}
    </TaskSettingsOverviewSection>
  );
};

export const TaskSettingsTriggeredItems = ({
  title,
  description,
  onBack,
  value,
  onChange,
  expTpl,
  triggeredItems,
  onGoToOtherItem
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title, // TODO Triggered Items - Add beta batch here
          description,
          icon: ICON as any
        }}
        onBack={onBack}
      />

      <SettingsSection>
        <TriggeredItemsList
          triggeredItems={triggeredItems}
          tplContext={expTpl}
          onGoToOtherItem={onGoToOtherItem}
        />
      </SettingsSection>
    </SideContainer>
  );
};
